import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Package, Image, TrendingUp, ArrowRight, MessageSquare } from "lucide-react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [token]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMediaUrl = (mediaPath) => {
    if (!mediaPath) return null;
    if (mediaPath.startsWith("http")) return mediaPath;
    return `${BACKEND_URL}${mediaPath}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-royal-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalProducts = stats?.total_products || 0;
  const retailProducts = stats?.retail_products || 0;
  const wholesaleProducts = stats?.wholesale_products || 0;
  const homepageMediaCount = stats?.homepage_media_count || 0;
  const totalEnquiries = stats?.total_enquiries || 0;
  const recentProducts = stats?.recent_products || [];

  return (
    <div className="p-6 md:p-8" data-testid="admin-dashboard">
      <div className="mb-8">
        <p className="font-display text-xs tracking-[0.3em] uppercase text-royal-gold mb-2">Overview</p>
        <h1 className="font-serif text-3xl text-cream">Dashboard</h1>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard
          icon={Package}
          iconColor="text-royal-gold"
          label="Total Products"
          value={totalProducts}
          showTrend
        />
        <StatCard
          icon={Package}
          iconColor="text-royal-gold"
          label="Retail"
          value={retailProducts}
        />
        <StatCard
          icon={Package}
          iconColor="text-silver"
          label="Wholesale"
          value={wholesaleProducts}
        />
        <StatCard
          icon={Image}
          iconColor="text-cream/60"
          label="Homepage Media"
          value={homepageMediaCount}
        />
        <StatCard
          icon={MessageSquare}
          iconColor="text-[#25D366]"
          label="Enquiries"
          value={totalEnquiries}
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Link
          to="/admin/products"
          data-testid="quick-action-products"
          className="bg-charcoal/50 border border-royal-gold/10 p-6 hover:border-royal-gold/30 transition-colors flex items-center justify-between group"
        >
          <div>
            <h3 className="font-serif text-lg text-cream mb-1">Manage Products</h3>
            <p className="font-body text-sm text-cream/50">Add, edit, or delete</p>
          </div>
          <ArrowRight size={20} className="text-cream/40 group-hover:text-royal-gold transition-colors" />
        </Link>

        <Link
          to="/admin/homepage-media"
          data-testid="quick-action-media"
          className="bg-charcoal/50 border border-royal-gold/10 p-6 hover:border-royal-gold/30 transition-colors flex items-center justify-between group"
        >
          <div>
            <h3 className="font-serif text-lg text-cream mb-1">Homepage Media</h3>
            <p className="font-body text-sm text-cream/50">Hero & banner images</p>
          </div>
          <ArrowRight size={20} className="text-cream/40 group-hover:text-royal-gold transition-colors" />
        </Link>

        <Link
          to="/admin/enquiries"
          data-testid="quick-action-enquiries"
          className="bg-charcoal/50 border border-royal-gold/10 p-6 hover:border-royal-gold/30 transition-colors flex items-center justify-between group"
        >
          <div>
            <h3 className="font-serif text-lg text-cream mb-1">View Enquiries</h3>
            <p className="font-body text-sm text-cream/50">Customer leads</p>
          </div>
          <ArrowRight size={20} className="text-cream/40 group-hover:text-royal-gold transition-colors" />
        </Link>
      </div>

      {/* Recent Products */}
      <div className="bg-charcoal/30 border border-royal-gold/10">
        <div className="p-6 border-b border-royal-gold/10 flex items-center justify-between">
          <h2 className="font-serif text-xl text-cream">Recent Products</h2>
          <Link
            to="/admin/products"
            className="font-body text-sm text-royal-gold hover:underline"
          >
            View all
          </Link>
        </div>

        {recentProducts.length > 0 ? (
          <div className="divide-y divide-royal-gold/10">
            {recentProducts.map((product) => (
              <ProductRow key={product.id} product={product} getMediaUrl={getMediaUrl} />
            ))}
          </div>
        ) : (
          <div className="p-12 text-center text-cream/50">
            <Package size={40} className="mx-auto mb-3 text-cream/30" />
            <p className="font-body text-sm">No products yet</p>
            <Link
              to="/admin/products"
              className="font-body text-sm text-royal-gold hover:underline mt-2 inline-block"
            >
              Add your first product
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

function StatCard({ icon: Icon, iconColor, label, value, showTrend }) {
  return (
    <div className="bg-charcoal/50 border border-royal-gold/10 p-5">
      <div className="flex items-center justify-between mb-3">
        <Icon size={20} className={iconColor} />
        {showTrend && <TrendingUp size={14} className="text-green-500" />}
      </div>
      <p className="font-body text-xs text-cream/50 mb-1">{label}</p>
      <p className="font-serif text-2xl text-cream">{value}</p>
    </div>
  );
}

function ProductRow({ product, getMediaUrl }) {
  const primaryMedia = product.media && product.media.length > 0 ? product.media[0] : null;

  return (
    <div className="p-4 flex items-center space-x-4">
      <div className="w-14 h-14 bg-velvet flex-shrink-0 overflow-hidden">
        {primaryMedia ? (
          <img
            src={getMediaUrl(primaryMedia.media_path)}
            alt={product.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-cream/30">
            <Package size={20} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body text-sm text-cream truncate">{product.title}</p>
        <p className="font-body text-xs text-cream/50 capitalize">{product.category}</p>
      </div>
      <span className={`px-3 py-1 text-xs font-display tracking-wider uppercase ${product.is_featured
        ? "bg-royal-gold/20 text-royal-gold"
        : "bg-cream/10 text-cream/50"
        }`}>
        {product.is_featured ? "Featured" : "Regular"}
      </span>
    </div>
  );
}

export default AdminDashboard;
